import { useSessionKey } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { HistoryDetail } from "@patients/HistoryDetail";
import { buildHistory } from "@patients/historyEntries";
import { PatientBand } from "@patients/PatientBand";
import { PatientHistory } from "@patients/PatientHistory";
import { patientsClient } from "@patients/patientsClient";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  PanelError,
  Skeleton,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { viewsListQueryOptions } from "@views/viewsQueries";
import { usePatientVisitHistory } from "@visits/visitsQueries";
import { useMemo, useState } from "react";

const PANEL = "rounded-md border border-rule bg-card shadow-sheet";
const COLUMNS = "grid-cols-[320px_minmax(0,1fr)]";

export const PatientPage = ({ patientId }: { patientId: string }) => {
  return (
    <AsyncBoundary
      key={patientId}
      skeleton={<PatientSkeleton />}
      fallback={(retry) => (
        <div className={`${PANEL} p-6`}>
          <PanelError label="patient" onRetry={retry} />
        </div>
      )}
    >
      <PatientContent patientId={patientId} />
    </AsyncBoundary>
  );
};

const PatientContent = ({ patientId }: { patientId: string }) => {
  const sessionKey = useSessionKey();
  const { data: patient } = useSuspenseQuery({
    queryKey: ["patients", patientId],
    queryFn: () => patientsClient.get(patientId),
  });
  const visits = usePatientVisitHistory(sessionKey, patientId);
  const views = useQuery(viewsListQueryOptions(sessionKey, patientId));
  const { data: rooms } = useRoomsListQuery(sessionKey, undefined, true);

  const entries = useMemo(
    () => buildHistory(visits.data?.pages.flat() ?? [], views.data ?? []),
    [visits.data, views.data],
  );
  const [chosenId, setChosenId] = useState<string | null>(null);
  const selected = entries.find((e) => e.id === chosenId) ?? entries[0] ?? null;
  const roomName = (id: string | null) =>
    (id && rooms?.find((room) => room.id === id)?.name) || null;

  const pending = visits.isPending || views.isPending;
  const failed = visits.isError || views.isError;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PatientBand patient={patient} />
      <div className={`grid min-h-0 flex-1 ${COLUMNS} gap-4`}>
        <section
          aria-label="History"
          className={`${PANEL} flex min-h-0 flex-col px-[22px] pb-4 pt-5`}
        >
          {failed ? (
            <PanelError
              label="history"
              onRetry={() => {
                void visits.refetch();
                void views.refetch();
              }}
            />
          ) : pending ? (
            <SkeletonStack count={5} className="gap-3" itemClassName="h-16" />
          ) : (
            <PatientHistory
              entries={entries}
              selectedId={selected?.id ?? null}
              onSelect={setChosenId}
              roomName={roomName}
              complete={!visits.hasNextPage}
              loadingMore={visits.isFetchingNextPage}
              onLoadMore={() => void visits.fetchNextPage()}
            />
          )}
        </section>
        {selected ? (
          <HistoryDetail entry={selected} roomName={roomName} />
        ) : (
          <div className={PANEL} />
        )}
      </div>
    </div>
  );
};

const PatientSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col gap-4">
    <div className={`${PANEL} flex flex-col gap-3 px-7 py-4`}>
      <Skeleton className="h-10 w-96" />
      <Skeleton className="h-8" />
    </div>
    <div className={`grid min-h-0 flex-1 ${COLUMNS} gap-4`}>
      <div className={`${PANEL} px-[22px] pt-5`}>
        <SkeletonStack count={5} className="gap-3" itemClassName="h-16" />
      </div>
      <div className={`${PANEL} px-7 pt-5`}>
        <Skeleton className="h-8 w-72" />
      </div>
    </div>
  </div>
);
